import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import BrandLogo from './BrandLogo';

/**
 * RouteSuspense — wraps a lazy-loaded route's content in:
 *  1. <Suspense> with a brand-styled skeleton (no white flash)
 *  2. <ErrorBoundary> that resets on route change (key={pathname}) so a
 *     ChunkLoadError on one route does not poison subsequent navigations.
 *
 * Used for R217.0A code-splitting. The fallback covers chunk-load failures
 * caused by stale deploys, weak signal pitch-side, or ad-blockers.
 *
 * Retry strategy = full reload, because most chunk-load failures are stale
 * HTML pointing at hashed bundles that no longer exist after a deploy.
 */

function RouteSkeleton() {
  // R217.1 Sub-PR 1: keep a visible loader so users on slow connections aren't
  // staring at a frozen blank screen. We trimmed it from a full 60vh centered
  // spinner to a compact top-of-page loader. Sub-PR 2 will add page-level
  // Skeletons; we'll then revisit whether this fallback can shrink further.
  return (
    <div
      data-testid="route-suspense-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading page"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 10, padding: '24px 16px', color: 'var(--text-dim, #9A9AB2)',
        fontSize: '13px'
      }}
    >
      <BrandLogo variant="icon" height={24} decorative />
      <span
        aria-hidden="true"
        style={{
          width: 18, height: 18,
          border: '2px solid rgba(16, 185, 129, 0.25)',
          borderTopColor: 'var(--accent)',
          borderRadius: '50%',
          display: 'inline-block',
          animation: 'route-suspense-spin 0.8s linear infinite'
        }}
      />
      <span>Loading…</span>
      <style>{`
        @keyframes route-suspense-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="route-suspense-skeleton"] span[aria-hidden="true"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function ChunkLoadFallback() {
  return (
    <div
      role="alert"
      data-testid="route-suspense-chunk-error"
      style={{
        padding: '24px',
        margin: '24px auto',
        maxWidth: 480,
        border: '1px solid #ef4444',
        borderRadius: '8px',
        background: 'rgba(239, 68, 68, 0.08)',
        color: 'var(--text-primary, #fff)',
        fontSize: '14px',
        lineHeight: 1.5,
        textAlign: 'center'
      }}
    >
      <BrandLogo height={28} showBeta style={{ marginBottom: 16 }} />
      <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '15px' }}>
        Couldn't load this page
      </div>
      <div style={{ opacity: 0.85, fontSize: '13px', marginBottom: '14px' }}>
        You may be on an old version or a weak connection. Reload to get the latest version.
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '8px 18px',
          fontSize: '13px',
          fontWeight: 600,
          background: 'var(--accent)',
          color: '#0A0A0F',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}
      >
        Reload
      </button>
    </div>
  );
}

export default function RouteSuspense({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} fallback={<ChunkLoadFallback />}>
      <Suspense fallback={<RouteSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  );
}
